/**
 * @module deploy/netlify
 * @description SDK-free Netlify deployment slice contract.
 */

import type { DeployAdapter, DeploymentTarget } from "@geenius/adapters";

import {
  createProviderDeployAdapter,
  isProviderDeploymentTarget,
  type ProviderDeployAdapter,
  validateProviderDeploymentTarget,
} from "./provider-slice";

/**
 * Deployment target accepted by the Netlify provider slice.
 */
export type NetlifyDeploymentTarget = DeploymentTarget & {
  readonly provider: "netlify";
};

/**
 * Netlify-scoped deployment adapter. Downstream wiring may accept a neutral
 * adapter, but the wrapped public slice only accepts Netlify targets.
 */
export type NetlifyDeployAdapter = ProviderDeployAdapter<"netlify">;

/**
 * SDK-free manifest describing Netlify deployment capabilities and required
 * environment variables.
 */
export interface NetlifyDeployProviderManifest {
  readonly provider: "netlify";
  readonly displayName: string;
  readonly runtime: "sdk-free-contract";
  readonly capabilities: readonly string[];
  readonly environmentVariables: readonly string[];
}

/**
 * Netlify deploy contract bundle with its manifest, wrapped adapter, and
 * target validator.
 */
export interface NetlifyDeployContract {
  readonly provider: "netlify";
  readonly manifest: NetlifyDeployProviderManifest;
  readonly adapter: NetlifyDeployAdapter;
  validateTarget(target: unknown): NetlifyDeploymentTarget;
}

/**
 * Provider manifest for SDK-free Netlify deployment adapters.
 */
export const NETLIFY_DEPLOY_PROVIDER: NetlifyDeployProviderManifest = {
  provider: "netlify",
  displayName: "Netlify",
  runtime: "sdk-free-contract",
  capabilities: [
    "project-deployments",
    "rollback",
    "deployment-lookup",
    "deployment-list",
    "health-check",
  ],
  environmentVariables: ["NETLIFY_SITE_ID", "NETLIFY_AUTH_TOKEN"],
};

/**
 * Checks whether a deployment target belongs to the Netlify provider.
 */
export function isNetlifyDeploymentTarget(
  target: unknown,
): target is NetlifyDeploymentTarget {
  return isProviderDeploymentTarget("netlify", target);
}

/**
 * Validates and narrows a deployment target for the Netlify provider slice.
 */
export function validateNetlifyDeploymentTarget(
  target: unknown,
): NetlifyDeploymentTarget {
  return validateProviderDeploymentTarget("netlify", target);
}

/**
 * Wraps a generic deployment adapter with Netlify target and result guards.
 */
export function createNetlifyDeployAdapter(
  adapter: DeployAdapter,
): NetlifyDeployAdapter {
  return createProviderDeployAdapter({ provider: "netlify", adapter });
}

/**
 * Creates a Netlify deploy contract bundle from a provider-neutral adapter.
 */
export function createNetlifyDeployContract(
  adapter: DeployAdapter,
): NetlifyDeployContract {
  return {
    provider: "netlify",
    manifest: NETLIFY_DEPLOY_PROVIDER,
    adapter: createNetlifyDeployAdapter(adapter),
    validateTarget: validateNetlifyDeploymentTarget,
  };
}
